import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type RegistryTable = {
  id: number;
  domain: string;
  tableName: string;
  sourceSystem: string;
  sourceType: string;
  sourceTableName: string;
  collectYn: 'Y' | 'N';
  note: string | null;
  legacyTableSchema: string | null;
  legacyTableName: string | null;
  columnsBaselineVersion: number | null;
  /** legacy_import 로 적재된 행 — 보존·대조용이라 수정할 수 없다 */
  legacy: boolean;
  /** 목록에서만 온다 */
  columnCount?: number;
  /** 목록에서만 온다 — 변경 이력의 최신 non-null ticket_key (없으면 null) */
  ticketKey?: string | null;
  gap?: { source: GapSummary | null; lake: GapSummary | null };
  version: number;
  updatedAt: string;
  updatedBy: string;
};

/** gap 요약. state: ok|gap|partial|unknown|no_columns|excluded|unmapped|skipped|query|table_missing 등 (서버 8단계 판정) */
export type GapSummary = { state: string; count: number; check: number; info: number; reason?: string | null; sourceAsOf?: string | null };
export type GapRow = { columnName: string; verdict: string; gap: boolean; registryType: string | null; snapshotType: string | null; info: string[] };
export type GapDetail = Record<'source' | 'lake', GapSummary & { rows: GapRow[]; capturedAt?: string | null; targetRef?: string | null }>;

export type RegistryColumn = {
  id: number; columnName: string; dataType: string | null; collectYn: 'Y' | 'N'; note: string | null; status: 'active' | 'dropped';
  addedVersion: number; droppedVersion: number | null; lastTableVersion: number;
  added: { at: string; by: string; ticketKey: string | null; reason: string };
  dropped: { at: string; by: string; ticketKey: string | null; reason: string } | null;
};
export type ColumnIn = { columnName: string; dataType?: string | null; collectYn?: 'Y' | 'N'; note?: string | null };
export type ColumnsPatch = { add?: ColumnIn[]; drop?: string[]; update?: ColumnIn[] };
export type ColumnHistory = {
  columnName: string;
  incarnations: { columnId: number; status: string; addedVersion: number; droppedVersion: number | null }[];
  events: { tableVersion: number; action: string; key?: string; old: unknown; new: unknown; changedAt: string; changedBy: string; ticketKey: string | null; reason: string }[];
};

export type PrRef = { number: number; url: string; state: string; ciState?: string; mergeable?: string };
export type ManifestTicket = {
  id: number; ticketKey: string; domain: string; status: string; bundleId: number | null; botConflict: boolean; errorCode: string | null;
  requestedBy: string; requestedAt: string; tables: { collectTableId: number; tableName: string; ingestMode: 'I' | 'F' | null; outcome?: string }[];
};
export type ManifestBundle = {
  id: number; domain: string; manifestPath: string; repTicketKey: string; branch: string; status: string; errorCode: string | null; errorDetail: string | null;
  confirmedBy: string; confirmedAt: string; updatedAt: string; devPr: PrRef | null; promotePr: PrRef | null; tickets: string[] | ManifestTicket[];
  events?: { id: number; at: string; kind: string; from: string | null; to: string | null; detail: unknown }[];
};
export type ManifestReview = { tickets: ManifestTicket[]; failedTickets?: ManifestTicket[]; inFlight: ManifestBundle[]; capacity: { manifestPath: string; current: number; capacity: number } | null };
export type ManifestBadge = {
  tableId: number; status: string; ticketKey: string; botConflict: boolean; devPrNumber: number | null; devPrUrl: string | null;
  promotePrUrl: string | null; promotePrNumber: number | null; botPrNumber: number | null; botPrState: string | null; errorCode: string | null;
  ciState: string | null; mergeable: string | null;
};
/** `/tables/:id/prs` 의 티켓 행 (lh_manifest_ticket_view 의 컬럼 그대로 — snake_case). promote_* 는 admin 에게만 값이 온다 */
export type TablePrTicket = {
  id: number; ticket_key: string; status: string; display_status: string; bundle_id: number | null; bot_conflict: boolean; branch: string | null; manifest_path: string | null;
  bundle_error_code: string | null; bundle_error_detail: string | null; error_code: string | null; requested_at: string;
  dev_pr_number: number | null; dev_pr_url: string | null; dev_pr_state: string | null; dev_ci_state: string | null; dev_mergeable: string | null;
  promote_pr_number: number | null; promote_pr_url: string | null; promote_pr_state: string | null;
  bot_pr_number: number | null; bot_pr_url: string | null; bot_pr_state: string | null;
  bot_promote_pr_number: number | null; bot_promote_pr_url: string | null; bot_promote_pr_state: string | null;
};
export type TablePrEvent = { id: number; at: string; bundleId: number | null; ticketId: number | null; prId: number | null; kind: string; from: string | null; to: string | null; detail: Record<string, unknown> | null; actor: string };
export type ManifestTicketRef = { id: number; status: string; skipped?: 'TICKET_IN_FLIGHT' | 'DOMAIN_MISMATCH' };

export type RegistryHistory = {
  version: number;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown>;
  reason: string;
  ticketKey: string | null;
  changedBy: string;
  requestedBy: string | null;
  changedAt: string;
  /** 이 version 에서 바뀐 컬럼 (add/drop/update) */
  columns?: { columnId: number; columnName: string; action: string; old: Record<string, unknown> | null; new: Record<string, unknown> | null }[];
};

export type CreateTableRequest = {
  domain: string;
  tableName: string;
  sourceSystem: string;
  sourceType: string;
  sourceTableName: string;
  collectYn: 'Y' | 'N';
  note?: string;
  columns?: ColumnIn[];
  reason: string;
  ticketKey: string;
};

export type UpdateTableRequest = {
  version: number;
  changes?: Partial<Pick<RegistryTable, 'domain' | 'tableName' | 'collectYn'>> & { note?: string | null };
  columns?: ColumnsPatch;
  reason: string;
  ticketKey?: string;
};

/** 서버 body `{code, message}` 를 그대로 들고 있다. VERSION_CONFLICT·REGISTRY_DISABLED 등을 code 로 분기한다. */
export class RegistryApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'RegistryApiError';
  }
}

export type BatchItem =
  | ({ op: 'create' } & Omit<CreateTableRequest, 'reason' | 'ticketKey'>)
  | { op: 'update'; id: number; version: number; changes?: { collectYn?: 'Y' | 'N'; note?: string | null }; columns?: { add: ColumnIn[] } };

export const SERVICE_TYPES = ['Postgres', 'Mysql', 'Oracle', 'MongoDB'] as const;
export type SourceConnReq = { serviceType: (typeof SERVICE_TYPES)[number]; host: string; port: number; database: string; username: string; password: string };
export type SourceTable = { schema: string; name: string; columnCount: number };
export type ConnectionTest = { ok: boolean; message?: string; tables: SourceTable[] };
/** 등록된 connection(OM Database Service, RDB) — sourceSystem 은 서버가 매핑 규칙으로 계산한 레지스트리 값 */
export type ConnectionOption = { serviceName: string; serviceType: string; sourceSystem: string };
/** OM 컬럼(화면용). nullable 은 OM 에 따로 없고 constraint 로 온다 */
export type OmColumn = { name: string; dataType: string; constraint?: string; description?: string };
export type OmSourceTable = SourceTable & { columns: OmColumn[] };
export type ConnectionTables = { serviceName: string; sourceSystem: string; tables: OmSourceTable[]; ingested: boolean };
export type OmFieldChange = { name: string; oldValue?: string; newValue?: string };
export type OmVersion = {
  version: number; updatedAt: number | null; updatedBy: string | null; deleted: boolean;
  fieldsAdded: OmFieldChange[]; fieldsUpdated: OmFieldChange[]; fieldsDeleted: OmFieldChange[];
};
/** no_service = RDB 원천인데 OM 서비스가 없다, unsupported_source = RDB 가 아닌 원천(DPD-14025), not_found = 서비스에 이 테이블이 없다(미ingest),
 * truncated = 서비스 테이블 목록이 상한에서 끊겨 없다고 말할 수 없다, ambiguous = 일치하는 OM 테이블이 둘 이상 */
export type OmHistory =
  | { state: 'no_service' }
  | { state: 'unsupported_source' }
  | { state: 'not_found'; serviceName: string }
  | { state: 'truncated'; serviceName: string }
  | { state: 'ambiguous'; candidates: string[] }
  | { state: 'ok'; serviceName: string; omTableId: string; deleted: boolean; versions: OmVersion[] };

/** OM service ↔ domain·conn_id 한 행 (lh_source_connection). conn_id 는 이미 있는 Airflow connection 이름을 가리킬 뿐이다 */
export type ConnectionMap = { serviceName: string; domain: string | null; connId: string; version: number; conflictWith?: string[] };
/** 매핑 관리 표의 한 행 — RDB service 마다 하나(중복 제거 없음). connIdDefault = 저장된 매핑이 없어 기본값(db_<소문자>)을 보여준다 */
export type ConnectionMapping = {
  serviceName: string; serviceType: string; sourceSystem: string;
  domain: string | null; connId: string; connIdDefault: boolean; mapVersion: number | null;
  /** 실효 conn_id 가 같은 다른 service — 비어 있지 않으면 충돌 */
  conflictWith: string[];
};
export type SetConnectionMappingReq = { domain: string | null; connId: string; version: number | null; reason: string; ticketKey: string };
/** mapping = null 이면 기본 conn_id 가 다른 서비스와 겹쳐 연결하지 않았다 */
export type ConnectionResult = { serviceName: string; omState: 'created' | 'exists'; test: ConnectionTest; airflowPending: boolean; mapping?: ConnectionMap | null; mappingDomainMismatch?: boolean };

export interface LhRegistryApi {
  listTables(f: { q?: string; collectYn?: 'Y' | 'N'; gap?: 'source' | 'lake' | 'any' }): Promise<RegistryTable[]>;
  listTablesPage(f: { q?: string; collectYn?: 'Y' | 'N'; gap?: 'source' | 'lake' | 'any'; manifest?: 'waiting' | 'progress' | 'blocked' | 'failed'; limit: number; offset: number }): Promise<{ items: RegistryTable[]; total: number }>;
  history(id: number): Promise<RegistryHistory[]>;
  listDomains(): Promise<{ domain: string }[]>;
  addDomain(req: { domain: string; ticketKey: string }): Promise<{ domain: string }>;
  /** 저장하지 않는다 — 접속 test 와 원천 테이블 목록 */
  testConnection(req: SourceConnReq): Promise<ConnectionTest>;
  /** 등록된 connection 목록 — 비밀번호 없이 OM 에서 읽는다 */
  listConnections(omAccessToken?: string): Promise<ConnectionOption[]>;
  /** 매핑 관리 표용 — RDB service 를 중복 제거 없이 전부, domain·conn_id 와 함께 */
  listConnectionMappings(omAccessToken?: string): Promise<ConnectionMapping[]>;
  setConnectionMapping(serviceName: string, req: SetConnectionMappingReq, omAccessToken?: string): Promise<ConnectionMap>;
  /** OM 에 ingest 된 테이블 목록. ingested=false 면 아직 ingest 전 */
  connectionTables(serviceName: string, omAccessToken?: string): Promise<ConnectionTables>;
  /** OpenMetadata 에 Database Service 를 등록한다(이미 있으면 exists). test 실패는 등록을 막지 않는다 */
  createConnection(req: SourceConnReq & { domain: string; serviceName: string; ticketKey: string }, omAccessToken?: string): Promise<ConnectionResult>;
  /** 레지스트리 테이블에 대응하는 OM 테이블의 삭제 표시·버전 이력 (읽기 전용) */
  omHistory(id: number, omAccessToken?: string): Promise<OmHistory>;
  createTable(req: CreateTableRequest): Promise<RegistryTable & { manifestTicket?: ManifestTicketRef }>;
  updateTable(id: number, req: UpdateTableRequest): Promise<{ table: RegistryTable; columns: RegistryColumn[] }>;
  batch(req: { ticketKey: string; reason: string; items: BatchItem[] }): Promise<{ items: { table: RegistryTable; manifestTicket?: ManifestTicketRef }[] }>;
  listColumns(id: number): Promise<RegistryColumn[]>;
  columnHistory(id: number, name: string): Promise<ColumnHistory>;
  gap(id: number): Promise<GapDetail>;
  /** 실패하면 빈 객체 — 상태 칸만 "—" 로 둔다 */
  ticketStatuses(keys: string[]): Promise<Record<string, { status: string; category: string }>>;

  // manifest PR 묶음 — GitHub 설정이 없으면 서버가 503 MANIFEST_DISABLED
  manifestBadges(): Promise<ManifestBadge[]>;
  manifestReview(domain: string): Promise<ManifestReview>;
  manifestBundles(f: { domain?: string; status?: string }): Promise<ManifestBundle[]>;
  manifestBundle(id: number): Promise<ManifestBundle>;
  manifestConfirm(req: { domain: string; ticketIds: number[]; ingestModes: Record<string, 'I' | 'F'> }): Promise<{ bundleId: number }>;
  manifestExclude(bundleId: number, ticketId: number): Promise<void>;
  manifestPromote(bundleId: number): Promise<void>;
  manifestResume(bundleId: number): Promise<void>;
  manifestRetry(bundleId: number): Promise<void>;
  manifestRetryTicket(ticketId: number): Promise<void>;
  manifestSync(bundleId: number): Promise<ManifestBundle>;
  tablePrs(id: number): Promise<{ tickets: TablePrTicket[]; events: TablePrEvent[] }>;
}

export const lhRegistryApiRef = createApiRef<LhRegistryApi>({ id: 'plugin.lh-registry.service' });

const PLUGIN_ID = 'lh-registry';

const omHeader = (t?: string) => (t ? { 'x-om-authorization': `Bearer ${t}` } : undefined);

export class LhRegistryClient implements LhRegistryApi {
  constructor(private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {}

  private async call<T>(path: string, init?: { method: string; body?: unknown; headers?: Record<string, string> }): Promise<T> {
    const base = await this.deps.discoveryApi.getBaseUrl(PLUGIN_ID);
    const res = await this.deps.fetchApi.fetch(`${base}${path}`, {
      method: init?.method,
      headers: init?.body || init?.headers ? { ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers } : undefined,
      body: init?.body ? JSON.stringify(init.body) : undefined,
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new RegistryApiError(res.status, b.code ?? 'UNKNOWN', b.message ?? `요청이 실패했습니다 (HTTP ${res.status})`);
    }
    return res.json();
  }

  listTables({ q, collectYn, gap }: { q?: string; collectYn?: 'Y' | 'N'; gap?: 'source' | 'lake' | 'any' }) {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (collectYn) p.set('collectYn', collectYn);
    if (gap) p.set('gap', gap);
    return this.call<RegistryTable[]>(`/tables?${p}`);
  }
  listTablesPage({ q, collectYn, gap, manifest, limit, offset }: { q?: string; collectYn?: 'Y' | 'N'; gap?: 'source' | 'lake' | 'any'; manifest?: 'waiting' | 'progress' | 'blocked' | 'failed'; limit: number; offset: number }) {
    const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (q) p.set('q', q);
    if (collectYn) p.set('collectYn', collectYn);
    if (gap) p.set('gap', gap);
    if (manifest) p.set('manifest', manifest);
    return this.call<{ items: RegistryTable[]; total: number }>(`/tables?${p}`);
  }
  history(id: number) {
    return this.call<RegistryHistory[]>(`/tables/${id}/history`);
  }
  listDomains() {
    return this.call<{ domain: string }[]>('/domains');
  }
  addDomain(req: { domain: string; ticketKey: string }) {
    return this.call<{ domain: string }>('/domains', { method: 'POST', body: req });
  }
  testConnection(req: SourceConnReq) {
    return this.call<ConnectionTest>('/connections/test', { method: 'POST', body: req });
  }
  listConnections(omAccessToken?: string) {
    return this.call<ConnectionOption[]>('/connections', { method: 'GET', headers: omHeader(omAccessToken) });
  }
  listConnectionMappings(omAccessToken?: string) {
    return this.call<ConnectionMapping[]>('/connections/mappings', { method: 'GET', headers: omHeader(omAccessToken) });
  }
  setConnectionMapping(serviceName: string, req: SetConnectionMappingReq, omAccessToken?: string) {
    return this.call<ConnectionMap>(`/connections/${encodeURIComponent(serviceName)}/mapping`, { method: 'PUT', body: req, headers: omHeader(omAccessToken) });
  }
  omHistory(id: number, omAccessToken?: string) {
    return this.call<OmHistory>(`/tables/${id}/om-history`, { method: 'GET', headers: omHeader(omAccessToken) });
  }
  connectionTables(serviceName: string, omAccessToken?: string) {
    return this.call<ConnectionTables>(`/connections/${encodeURIComponent(serviceName)}/tables`, { method: 'GET', headers: omHeader(omAccessToken) });
  }
  createConnection(req: SourceConnReq & { domain: string; serviceName: string; ticketKey: string }, omAccessToken?: string) {
    // 로그인 사용자의 Keycloak access token — 서버는 OM 호출에만 쓰고 저장·로그하지 않는다. 없으면 헤더를 생략한다(서버가 모드별로 판단)
    return this.call<ConnectionResult>('/connections', {
      method: 'POST', body: req, headers: omAccessToken ? { 'x-om-authorization': `Bearer ${omAccessToken}` } : undefined,
    });
  }
  createTable(req: CreateTableRequest) {
    return this.call<RegistryTable & { manifestTicket?: ManifestTicketRef }>('/tables', { method: 'POST', body: req });
  }
  updateTable(id: number, req: UpdateTableRequest) {
    return this.call<{ table: RegistryTable; columns: RegistryColumn[] }>(`/tables/${id}`, { method: 'PATCH', body: req });
  }
  batch(req: { ticketKey: string; reason: string; items: BatchItem[] }) {
    return this.call<{ items: { table: RegistryTable; manifestTicket?: ManifestTicketRef }[] }>('/tables/batch', { method: 'POST', body: req });
  }
  listColumns(id: number) {
    return this.call<RegistryColumn[]>(`/tables/${id}/columns`);
  }
  columnHistory(id: number, name: string) {
    return this.call<ColumnHistory>(`/tables/${id}/columns/${encodeURIComponent(name)}/history`);
  }
  gap(id: number) {
    return this.call<GapDetail>(`/tables/${id}/gap`);
  }
  manifestBadges() {
    return this.call<ManifestBadge[]>('/manifest/badges');
  }
  manifestReview(domain: string) {
    return this.call<ManifestReview>(`/manifest/domains/${encodeURIComponent(domain)}/review`);
  }
  manifestBundles({ domain, status }: { domain?: string; status?: string }) {
    const p = new URLSearchParams();
    if (domain) p.set('domain', domain);
    if (status) p.set('status', status);
    return this.call<ManifestBundle[]>(`/manifest/bundles?${p}`);
  }
  manifestBundle(id: number) {
    return this.call<ManifestBundle>(`/manifest/bundles/${id}`);
  }
  manifestConfirm(req: { domain: string; ticketIds: number[]; ingestModes: Record<string, 'I' | 'F'> }) {
    return this.call<{ bundleId: number }>('/manifest/bundles', { method: 'POST', body: req });
  }
  async manifestExclude(bundleId: number, ticketId: number) {
    await this.call(`/manifest/bundles/${bundleId}/tickets/${ticketId}/exclude`, { method: 'POST', body: {} });
  }
  async manifestPromote(bundleId: number) {
    await this.call(`/manifest/bundles/${bundleId}/promote`, { method: 'POST', body: {} });
  }
  async manifestResume(bundleId: number) {
    await this.call(`/manifest/bundles/${bundleId}/resume`, { method: 'POST', body: {} });
  }
  async manifestRetry(bundleId: number) {
    await this.call(`/manifest/bundles/${bundleId}/retry`, { method: 'POST', body: {} });
  }
  async manifestRetryTicket(ticketId: number) {
    await this.call(`/manifest/tickets/${ticketId}/retry`, { method: 'POST', body: {} });
  }
  manifestSync(bundleId: number) {
    return this.call<ManifestBundle>(`/manifest/bundles/${bundleId}/sync`, { method: 'POST', body: {} });
  }
  tablePrs(id: number) {
    return this.call<{ tickets: TablePrTicket[]; events: TablePrEvent[] }>(`/tables/${id}/prs`);
  }
  async ticketStatuses(keys: string[]) {
    if (!keys.length) return {};
    try {
      return (await this.call<{ statuses: Record<string, { status: string; category: string }> }>(`/tickets/status?keys=${keys.join(',')}`)).statuses;
    } catch {
      return {};
    }
  }
}
